import { formatTimeAgo } from "../utils/dateUtils";
import styles from "./NotificationItem.module.css";
import AppImage from "@/components/AppImage";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import DoneIcon from "@mui/icons-material/Done";
import { markAsRead } from "../firebase/services/notificationServices";
import { handleFollowRequest } from "../firebase/services/followServices";

const NOTIFICATION_ICONS = {
  LIKE_REVIEW: FavoriteIcon,
  NEW_FOLLOWER: PersonAddIcon,
  FOLLOW_REQUEST: PersonAddOutlinedIcon,
  BOOK_CLUB_UPDATE: MenuBookIcon,
  READING_GOAL_ACHIEVED: EmojiEventsIcon,
  COMMENT_ON_REVIEW: ChatBubbleOutlineIcon,
  LIST_FOLLOWER: BookmarkAddIcon,
};

const ICON_TYPE_CLASSES = {
  LIKE_REVIEW: "iconLike",
  NEW_FOLLOWER: "iconFollower",
  FOLLOW_REQUEST: "iconFollower",
  BOOK_CLUB_UPDATE: "iconBook",
  READING_GOAL_ACHIEVED: "iconGoal",
  COMMENT_ON_REVIEW: "iconComment",
  LIST_FOLLOWER: "iconList",
};

export default function NotificationItem({ notification, onClick }) {
  const Icon = NOTIFICATION_ICONS[notification.type] || MenuBookIcon;
  const iconTypeClass = ICON_TYPE_CLASSES[notification.type] || "iconBook";

  // Don't render rejected follow requests at all
  if (
    notification.type === "FOLLOW_REQUEST" &&
    notification.metadata?.handled &&
    !notification.metadata?.accepted
  ) {
    return null;
  }

  const getMessage = () => {
    if (notification.type === "FOLLOW_REQUEST") {
      if (notification.metadata?.handled && notification.metadata?.accepted) {
        return `${notification.metadata.actorName} started following you`;
      }
      return notification.message;
    }
    return notification.message;
  };

  const handleMarkAsRead = async (e) => {
    e.stopPropagation();
    if (!notification.read) {
      try {
        await markAsRead(notification.id);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
  };

  const handleAcceptRequest = async (e) => {
    e.stopPropagation();
    try {
      await handleFollowRequest(
        notification.metadata.actorId,
        notification.userId,
        true
      );
      // Update the local notification state to show "started following you"
      notification.metadata.handled = true;
      notification.metadata.accepted = true;
    } catch (error) {
      console.error("Error accepting follow request:", error);
    }
  };

  const handleRejectRequest = async (e) => {
    e.stopPropagation();
    try {
      await handleFollowRequest(
        notification.metadata.actorId,
        notification.userId,
        false
      );
      // The notification will be hidden due to the null return above
      notification.metadata.handled = true;
      notification.metadata.accepted = false;
    } catch (error) {
      console.error("Error rejecting follow request:", error);
    }
  };

  return (
    <div
      className={`${styles.notificationItem} ${
        !notification.read ? styles.unread : ""
      }`}
      onClick={onClick}
    >
      <Icon className={`${styles.icon} ${styles[iconTypeClass]}`} />
      <div className={styles.content}>
        <p className={styles.message}>{getMessage()}</p>
        {notification.metadata?.actorImage && (
          <div className={styles.metadata}>
            <AppImage
              src={notification.metadata.actorImage}
              alt={notification.metadata.actorName || "User"}
              width={24}
              height={24}
              className={styles.actorImage}
            />
            <span>{notification.metadata.actorName}</span>
          </div>
        )}
        {notification.type === "FOLLOW_REQUEST" &&
          !notification.metadata?.handled && (
            <div className={styles.actionButtons}>
              <button
                className={`${styles.actionButton} ${styles.acceptButton}`}
                onClick={handleAcceptRequest}
                aria-label="Accept follow request"
              >
                <PersonAddIcon className={styles.buttonIcon} />
                Accept
              </button>
              <button
                className={`${styles.actionButton} ${styles.rejectButton}`}
                onClick={handleRejectRequest}
                aria-label="Reject follow request"
              >
                Reject
              </button>
            </div>
          )}
        <span className={styles.timestamp}>
          {formatTimeAgo(notification.createdAt)}
        </span>
      </div>
      {!notification.read && (
        <button
          className={styles.markAsReadButton}
          onClick={handleMarkAsRead}
          title="Mark as read"
        >
          <DoneIcon />
        </button>
      )}
    </div>
  );
}
